import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, CheckCircle2, ShieldCheck, Clock, Phone, Mail } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api, BUSINESS } from "@/lib/api";

const TIME_SLOTS = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM",
  "4:00 PM", "5:00 PM", "6:00 PM",
];

export default function BookingPage() {
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service_type: "",
    date: undefined,
    time: "",
    address: "",
    description: "",
  });

  useEffect(() => {
    api
      .get("/services")
      .then((r) => setServices(r.data.services || []))
      .catch(() =>
        setServices([
          "TV Mounting", "General Mounting", "Furniture Assembly", "Drywall",
          "Painting", "Moving", "Junk Removal", "Carpentry", "Electrical",
          "Plumbing",
        ]),
      );
  }, []);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service_type || !form.date || !form.time || !form.address) {
      toast.error("Please fill all required fields.");
      return;
    }
    const preferred_datetime = `${format(form.date, "yyyy-MM-dd")} ${form.time}`;
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        name: form.name,
        phone: form.phone,
        email: form.email,
        service_type: form.service_type,
        preferred_datetime,
        address: form.address,
        description: form.description,
      });
      setSuccess(true);
      toast.success("Booking received! We’ll reach out shortly.");
      setForm({
        name: "", phone: "", email: "", service_type: "",
        date: undefined, time: "", address: "", description: "",
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit booking.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left column — trust */}
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">
              Book a Service
            </p>
            <h1 className="pf-heading mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900">
              Tell us what needs fixing.
            </h1>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Fill out the form and we’ll confirm your appointment within the
              hour. Servicing Fulton, DeKalb, Cobb, Gwinnett and Forsyth counties — open {BUSINESS.hours.toLowerCase()}.
            </p>

            <div className="mt-10 space-y-5">
              {[
                { icon: ShieldCheck, label: "Insured & vetted technicians" },
                { icon: Clock, label: "Same-day appointments available" },
                { icon: CheckCircle2, label: "Transparent, upfront pricing" },
              ].map((t) => {
                const Icon = t.icon;
                return (
                  <div key={t.label} className="flex items-start gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <p className="text-sm text-slate-700 mt-1">{t.label}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 p-6 rounded-2xl bg-slate-900 text-white">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-400">
                Prefer to call?
              </p>
              <p className="mt-2 text-lg flex items-center gap-2">
                <Phone className="h-4 w-4" />
                <a href={BUSINESS.phoneHref} data-testid="booking-phone">
                  {BUSINESS.phone}
                </a>
              </p>
              <p className="mt-1 text-sm text-slate-300 flex items-center gap-2">
                <Mail className="h-4 w-4" /> {BUSINESS.email}
              </p>
            </div>
          </div>

          {/* Right column — form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-100 rounded-3xl p-8 md:p-10 shadow-sm">
              {success ? (
                <div data-testid="booking-success" className="text-center py-10">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
                  <h2 className="pf-heading mt-5 text-2xl font-semibold text-slate-900">
                    Booking received!
                  </h2>
                  <p className="mt-3 text-slate-600">
                    We’ll be in touch shortly to confirm your appointment.
                  </p>
                  <Button
                    onClick={() => setSuccess(false)}
                    data-testid="booking-new-btn"
                    className="mt-8 bg-slate-900 hover:bg-blue-700 text-white rounded-xl"
                  >
                    Book another service
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="booking-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium text-slate-700">Full name *</Label>
                      <Input
                        id="name"
                        data-testid="booking-input-name"
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        placeholder="Jane Doe"
                        className="mt-2 h-11"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-medium text-slate-700">Phone *</Label>
                      <Input
                        id="phone"
                        data-testid="booking-input-phone"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="(404) 555-0123"
                        className="mt-2 h-11"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="booking-input-email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@example.com"
                      className="mt-2 h-11"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Service type *</Label>
                      <Select
                        value={form.service_type}
                        onValueChange={(v) => update("service_type", v)}
                      >
                        <SelectTrigger
                          data-testid="booking-select-service"
                          className="mt-2 h-11"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700">Preferred date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid="booking-date-trigger"
                            className="mt-2 w-full h-11 flex items-center justify-between px-3 rounded-md border border-input bg-white text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <span>{form.date ? format(form.date, "PPP") : "Pick a date"}</span>
                            <CalendarIcon className="h-4 w-4 text-slate-400" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={form.date}
                            onSelect={(d) => update("date", d)}
                            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                            data-testid="booking-calendar"
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-slate-700">Preferred time *</Label>
                    <Select value={form.time} onValueChange={(v) => update("time", v)}>
                      <SelectTrigger data-testid="booking-select-time" className="mt-2 h-11">
                        <SelectValue placeholder="Pick a time slot" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`time-option-${t}`}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="address" className="text-sm font-medium text-slate-700">Service address *</Label>
                    <Input
                      id="address"
                      data-testid="booking-input-address"
                      value={form.address}
                      onChange={(e) => update("address", e.target.value)}
                      placeholder="123 Peachtree St, Atlanta, GA"
                      className="mt-2 h-11"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description" className="text-sm font-medium text-slate-700">
                      Job description
                    </Label>
                    <Textarea
                      id="description"
                      data-testid="booking-input-description"
                      value={form.description}
                      onChange={(e) => update("description", e.target.value)}
                      placeholder="Tell us a little about what needs fixing..."
                      rows={4}
                      className="mt-2"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="booking-submit-btn"
                    className="w-full h-12 rounded-xl bg-slate-900 hover:bg-blue-700 text-white text-base font-semibold"
                  >
                    {submitting ? "Submitting…" : "Submit Booking"}
                  </Button>
                  <p className="text-xs text-slate-400 text-center">
                    We’ll only use your info to confirm and complete this service.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
